import React, { ReactNode } from 'react';
import { Box, Collapse, Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography } from '@material-ui/core';
import {
  Cancel,
  CheckCircle,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  RemoveCircle,
  WatchLaterSharp,
  Close as CloseIcon,
  EditOutlined,
  AddCircleOutline,
} from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { darken } from '@material-ui/core/styles';
import { unpackToDate, unpackToTime } from '@campfire/hot-date';
import { TabletButton } from '@campfire/tablet-button';
import {
  ActivityReportGetActivityReport_vm_activityReport_sessionReports as SessionReportType,
  ActivityReportGetActivityReport_vm_activityReport_sessionReports_individualSessionReports as IndividualSessionReportType,
  ActivityReportGetActivityReport_vm_activityReport_sessionReports_attendances as AttendanceType,
  ActivityReportGetActivityReport_vm_activity_allSessions as SessionType,
  ActivityReportGetActivityReport_vm_activity_publishedRoster as PublishedRosterType,
} from '../__generated__/ActivityReportGetActivityReport';
import {
  getFlexibleSessionDisplayName,
  getFlexibleSessionDisplayTimes,
} from '../../../program-manager/rosters/main-section/roster/flexible-session-utils';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { Avatar } from '../../user-profile/components/Avatar';
import { useUser } from '../../../../global/auth/useUser';
import { ActivityReport } from '../../../program-manager/rosters/main-section/roster/roster-table/ActivityReport';
import { SessionReportFormV2 } from '../../../program-manager/activities/activity-timeline/past/report/form/SessionReportFormV2';
import { IndividualSessionReportForm } from '../../../program-manager/activities/activity-timeline/past/report/form/IndividualSessionReportForm';
import { SessionReportOption } from './SessionReportOption';
import {
  useCancelSessionReportFetch,
  useUnOmitSessionReportFetch,
} from '../../../program-manager/activities/activity-timeline/activity-timeline-actions';
import { useSnackbar } from '../../../../global/config/useSnackbar';

const useStyles = (color: string) => {
  const { bgColor, textColor, iconColor, buttonColor } = (() => {
    switch (color) {
      case 'completed':
        return { bgColor: '#ECFDF5', textColor: '#23735C', iconColor: '#34d399', buttonColor: '#76ddac' };
      case 'incomplete':
        return { bgColor: '#FEF2F2', textColor: '#991B1B', iconColor: '#F87171', buttonColor: '#ed9b9b' };
      case 'ad-hoc-available':
        return { bgColor: '#F0F9FF', textColor: '#0369A1', iconColor: '#0EA5E9', buttonColor: '#38BDF8' };
      default:
        return { bgColor: '#F2F2F2', textColor: '#545454', iconColor: '#646464', buttonColor: '#9e9e9e' };
    }
  })();

  return makeStyles(() =>
    createStyles({
      container: {
        display: 'flex',
        paddingLeft: 22,
        paddingTop: 20,
        paddingBottom: 20,
        paddingRight: 11,
        alignItems: 'center',
        backgroundColor: bgColor,
        cursor: 'pointer',
      },
      icon: {
        color: iconColor,
        fontSize: '1.5rem',
      },
      textContainer: {
        marginLeft: 12,
        flex: 1,
        '&>h5': {
          color: textColor,
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: 1.5,
        },
        '&>p': {
          color: textColor,
          fontWeight: 400,
          fontSize: 12,
          lineHeight: '10px',
        },
      },
      button: {
        backgroundColor: buttonColor,
        color: 'white',
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 4,
        paddingRight: 4,
        fontSize: '1.5rem',
        borderRadius: 6,
        '&:hover': {
          backgroundColor: darken(buttonColor, 0.1),
        },
        alignSelf: 'center',
      },
    })
  )();
};

interface CampfireAccordionProps {
  icon: React.ReactElement;
  title: string;
  subTitle: string;
  onClick: () => void;
  color: string;
  disabled?: boolean;
}
export function CampfireAccordion({ icon, title, subTitle, onClick, color, disabled }: CampfireAccordionProps) {
  const classes = useStyles(color);
  const { isMobile } = useCampfireTheme();

  return (
    <Box className={classes.container} onClick={onClick} style={{ cursor: !disabled ? 'pointer' : 'inherit' }}>
      {React.cloneElement(icon, { className: classes.icon })}
      <Box className={classes.textContainer}>
        <Typography variant='h5'>{title}</Typography>
        <Typography variant='body1' style={{ lineHeight: isMobile ? '18px' : 'inherit' }}>
          {subTitle}
        </Typography>
      </Box>
      {!disabled ? (
        <IconButton className={classes.button} onClick={onClick}>
          <ChevronRight />
        </IconButton>
      ) : null}
    </Box>
  );
}

export const ActivityReportDetailDialog = ({
  session,
  activityName,
  activityDate,
  publishedRoster,
  sessionReport,
  onEdit,
  dialogOpen,
  onDialogClose,
  isEditting,
  setIsEditting,
  dialogHeaderOption,
  isAdHoc,
  orgAllowCICO,
  forceMainReport,
}: {
  dialogOpen: boolean;
  onDialogClose: () => void;
  onEdit: () => void;
  session: SessionType;
  sessionReport?: SessionReportType;
  activityDate: string;
  activityName: string;
  publishedRoster: PublishedRosterType | null;
  isEditting: boolean;
  setIsEditting: (isEditting: boolean) => void;
  dialogHeaderOption?: ReactNode;
  isAdHoc?: boolean;
  orgAllowCICO?: boolean;
  forceMainReport?: boolean;
}) => {
  const { isMobile, theme } = useCampfireTheme();
  const {
    user: { userIdentityService, preferredName, lastName, avatarUrl },
    getVolunteerIdentity,
  } = useUser();
  const [isEditingIndividual, setIsEditingIndividual] = React.useState(false);

  const isManager =
    userIdentityService.isVmAdmin || userIdentityService.isProgramManager || userIdentityService.isActivityLeader;

  const hasIndividualReports = !!session.individualReports;
  const { volunteerId } = getVolunteerIdentity();
  const volunteerProfile = { preferredName, lastName, avatarUrl };
  const effectiveAllowCICO = sessionReport
    ? sessionReport.CICOs?.length > 0 || sessionReport.allowCICO || session.activity?.allowCICO || orgAllowCICO || false
    : session.activity?.allowCICO ?? orgAllowCICO ?? false;
  const myIndividualReport =
    hasIndividualReports && !isManager
      ? sessionReport?.individualSessionReports?.find((r) => r.volunteer.volunteerId === volunteerId)
      : undefined;

  const existingAttendance =
    sessionReport?.attendances?.some(
      (a): a is Extract<AttendanceType, { __typename: 'VOLUNTEER_VolunteerAttendanceType' }> =>
        a.__typename === 'VOLUNTEER_VolunteerAttendanceType' && a.volunteer?.volunteerId === volunteerId
    ) ?? false;

  const existingCICORecord = sessionReport?.CICOs?.find((c) => c.volunteer?.volunteerId === volunteerId);
  const existingCICO = existingCICORecord
    ? {
        checkIn: existingCICORecord.checkIn ? unpackToTime(existingCICORecord.checkIn).toJSDate() : ('' as Date | ''),
        checkOut: existingCICORecord.checkOut
          ? unpackToTime(existingCICORecord.checkOut).toJSDate()
          : ('' as Date | ''),
      }
    : undefined;

  const sessionDisplayInfo = getFlexibleSessionDisplayName(session, activityDate);
  const sessionDisplayTimes = getFlexibleSessionDisplayTimes(session, activityDate, (time) =>
    unpackToTime(time).toFormat('h:mm a')
  );

  const handleCancel = () => {
    onDialogClose();
    setIsEditting(false);
    setIsEditingIndividual(false);
  };
  const handleClose = () => {
    onDialogClose();
    setIsEditting(false);
    setIsEditingIndividual(false);
  };

  const onSuccess = () => {
    onEdit();
    onDialogClose();
    setIsEditting(false);
    setIsEditingIndividual(false);
  };

  const onIndividualSuccess = () => {
    onEdit();
    setIsEditting(false);
    setIsEditingIndividual(false);
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth='sm'
      style={{ zIndex: 1300 }}
    >
      <DialogTitle>
        <Grid
          container
          justify={'space-between'}
          style={{
            paddingBottom: '16px',
            paddingLeft: '8px',
            borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
          }}
        >
          <Grid item xs>
            <Box>
              <Typography
                style={{
                  color: theme.color.grey.neutral500,
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                }}
              >
                {activityName}
              </Typography>
              {sessionDisplayInfo.isCustom && (
                <Typography
                  style={{
                    color: theme.color.grey.neutralBrand500,
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '16px',
                  }}
                >
                  {sessionDisplayInfo.originalName}
                </Typography>
              )}
              <Typography
                style={{
                  color: theme.color.grey.neutral500,
                  fontSize: '24px',
                  fontWeight: 600,
                  lineHeight: '30px',
                }}
              >
                {sessionDisplayInfo.displayName}
              </Typography>
              <Typography style={{ color: theme.color.grey.neutral500, fontSize: '14px', paddingTop: '8px' }}>
                {sessionDisplayTimes
                  ? `${unpackToDate(activityDate).toFormat('EEEE, DD')} - Scheduled: ${sessionDisplayTimes}`
                  : unpackToDate(activityDate).toFormat('EEEE, DD')}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box display='flex' alignItems='center'>
              {!isEditting &&
                !isEditingIndividual &&
                sessionReport &&
                !(hasIndividualReports && !isManager && myIndividualReport) && (
                  <TabletButton
                    variant='outlined'
                    color='primary'
                    startIcon={<EditOutlined style={{ fontSize: 14 }} />}
                    style={{ boxSizing: 'border-box', borderRadius: '4px', padding: '2px 8px', fontWeight: 600 }}
                    onClick={() => setIsEditting(true)}
                  >
                    Edit Report
                  </TabletButton>
                )}
              <Box>{dialogHeaderOption}</Box>
              <IconButton key='close' aria-label='Close' color='inherit' onClick={handleClose}>
                <CloseIcon color='action' />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ marginBottom: '16px' }}>
        {hasIndividualReports && !isManager && !forceMainReport ? (
          <Box>
            {isEditting || isEditingIndividual ? (
              <IndividualSessionReportForm
                session={session}
                activityDate={activityDate}
                existingFieldValues={myIndividualReport?.fieldValues}
                onSubmit={onIndividualSuccess}
                onCancel={handleCancel}
                allowCICO={effectiveAllowCICO}
                existingAttendance={existingAttendance}
                existingCICO={existingCICO}
                volunteerProfile={volunteerProfile}
              />
            ) : myIndividualReport ? (
              <IndividualSessionReportForm
                session={session}
                activityDate={activityDate}
                existingFieldValues={myIndividualReport.fieldValues}
                readOnly
                onEdit={() => setIsEditingIndividual(true)}
                allowCICO={effectiveAllowCICO}
                existingAttendance={existingAttendance}
                existingCICO={existingCICO}
                volunteerProfile={volunteerProfile}
              />
            ) : (
              <IndividualSessionReportForm
                session={session}
                activityDate={activityDate}
                onSubmit={onIndividualSuccess}
                onCancel={handleCancel}
                allowCICO={effectiveAllowCICO}
                volunteerProfile={volunteerProfile}
              />
            )}
          </Box>
        ) : isEditting ? (
          <Box>
            <SessionReportFormV2
              session={session}
              publishedRoster={publishedRoster}
              activityDate={activityDate}
              onCancelClick={handleCancel}
              onSubmitSuccess={onSuccess}
              sessionReport={sessionReport}
              isAdHoc={isAdHoc}
              orgAllowCICO={orgAllowCICO}
            />
          </Box>
        ) : sessionReport ? (
          <ActivityReport session={{ reports: [sessionReport] }} />
        ) : (
          <Box display='flex' justifyContent='center' alignItems='center' py={6}>
            <Typography variant='body2' color='textSecondary'>
              This session report has been omitted.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface CompletedSessionReportProps {
  session: SessionType;
  sessionReport: SessionReportType;
  onEdit: () => void;
  activityDate: string;
  activityName: string;
  publishedRoster: PublishedRosterType | null;
  isAdHoc?: boolean;
  orgAllowCICO?: boolean;
}
export function CompletedSessionReport({
  session,
  sessionReport,
  onEdit,
  activityDate,
  activityName,
  publishedRoster,
  isAdHoc,
  orgAllowCICO,
}: CompletedSessionReportProps) {
  const { profile } = sessionReport.submittedBy;
  const submitssionName = `${profile.preferredName} ${profile.lastName}`;
  const submittedDate = unpackToDate(sessionReport.submissionDate);
  const [isEditting, setIsEditting] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  // Get flexible session display info
  const sessionDisplayInfo = getFlexibleSessionDisplayName(session, activityDate);
  const onDialogClose = () => {
    setDialogOpen(false);
  };
  const subTitle =
    session?.reportType === null && session?.autoReport && profile?.email === 'support@volaby.org'
      ? `Automatically generated on ${submittedDate.toFormat('dd LLLL yyyy')}`
      : `Completed by ${submitssionName}, on ${submittedDate.toFormat('dd LLLL yyyy')}`;

  return (
    <React.Fragment>
      <CampfireAccordion
        title={sessionDisplayInfo.displayName}
        subTitle={subTitle}
        icon={<CheckCircle />}
        onClick={() => setDialogOpen(true)}
        color='completed'
      />
      {dialogOpen && (
        <ActivityReportDetailDialog
          session={session}
          activityName={activityName}
          activityDate={activityDate}
          publishedRoster={publishedRoster}
          sessionReport={sessionReport}
          isEditting={isEditting}
          setIsEditting={setIsEditting}
          onEdit={onEdit}
          dialogOpen={dialogOpen}
          onDialogClose={onDialogClose}
          isAdHoc={isAdHoc}
          orgAllowCICO={orgAllowCICO}
        />
      )}
    </React.Fragment>
  );
}

interface IncompleteReportProps {
  session: SessionType;
  activityDate: string;
  activityName: string;
  publishedRoster: PublishedRosterType | null;
  sessionReport?: SessionReportType;
  onEdit?: () => void;
  variant?: string;
  orgAllowCICO?: boolean;
}

export const IncompletedSessionReport = React.memo(function IncompletedSessionReportComponent({
  session,
  activityDate,
  activityName,
  publishedRoster,
  sessionReport,
  onEdit,
  variant = 'incomplete',
  orgAllowCICO,
}: IncompleteReportProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const onDialogClose = () => setDialogOpen(false);
  const omitSessionReport = useCancelSessionReportFetch();

  // Get flexible session display info
  const sessionDisplayInfo = getFlexibleSessionDisplayName(session, activityDate);

  const { setSnackbar } = useSnackbar();

  const onOmit = () => {
    omitSessionReport
      .run({
        sessionId: session.sessionId,
        activityDate: activityDate,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to omit session report',
            variant: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Omit session report success',
            variant: 'success',
          });
          onEdit();
        }
      });
  };

  return (
    <React.Fragment>
      <CampfireAccordion
        title={sessionDisplayInfo.displayName}
        subTitle={variant === 'ad-hoc-available' ? 'Click to add' : 'Incomplete'}
        icon={variant === 'ad-hoc-available' ? <AddCircleOutline /> : <Cancel />}
        onClick={() => setDialogOpen(true)}
        color={variant}
      />
      {dialogOpen && (
        <ActivityReportDetailDialog
          session={session}
          activityName={activityName}
          activityDate={activityDate}
          publishedRoster={publishedRoster}
          sessionReport={sessionReport}
          isEditting
          setIsEditting={() => {}}
          onEdit={onEdit}
          dialogOpen={dialogOpen}
          onDialogClose={onDialogClose}
          dialogHeaderOption={<SessionReportOption onOmit={onOmit} />}
          orgAllowCICO={orgAllowCICO}
        />
      )}
    </React.Fragment>
  );
});

interface CancelledReportProps {
  session: SessionType;
  activityDate: string;
  activityName: string;
  publishedRoster: PublishedRosterType | null;
  onEdit?: () => void;
  isAdHoc?: boolean;
  orgAllowCICO?: boolean;
}

export function CancelledSessionReport({
  session,
  activityDate,
  activityName,
  publishedRoster,
  onEdit,
  isAdHoc,
  orgAllowCICO,
}: CancelledReportProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const onDialogClose = () => setDialogOpen(false);

  // Get flexible session display info
  const sessionDisplayInfo = getFlexibleSessionDisplayName(session, activityDate);

  const unOmitSessionReport = useUnOmitSessionReportFetch();
  const { setSnackbar } = useSnackbar();

  const onUnOmit = () => {
    unOmitSessionReport
      .run({
        sessionId: session.sessionId,
        activityDate,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to remove session report omission',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Un-Omitted session report',
          variant: 'success',
        });
        onEdit();
      });
  };

  return (
    <>
      <CampfireAccordion
        title={sessionDisplayInfo.displayName}
        subTitle={'Omitted'}
        icon={<RemoveCircle />}
        onClick={() => setDialogOpen(true)}
        color='cancelled'
      />
      {dialogOpen && (
        <ActivityReportDetailDialog
          session={session}
          activityName={activityName}
          activityDate={activityDate}
          publishedRoster={publishedRoster}
          isEditting={false}
          setIsEditting={() => {}}
          onEdit={onEdit}
          dialogOpen={dialogOpen}
          onDialogClose={onDialogClose}
          dialogHeaderOption={<SessionReportOption onUnOmit={onUnOmit} />}
          isAdHoc={isAdHoc}
          orgAllowCICO={orgAllowCICO}
        />
      )}
    </>
  );
}

export function NulledSessionReport({
  session,
  activityDate,
  activityName,
  publishedRoster,
  onEdit,
  isAdHoc,
  orgAllowCICO,
}: CancelledReportProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const onDialogClose = () => setDialogOpen(false);
  const omitSessionReport = useCancelSessionReportFetch();

  // Get flexible session display info
  const sessionDisplayInfo = getFlexibleSessionDisplayName(session, activityDate);

  const { setSnackbar } = useSnackbar();

  const onOmit = () => {
    omitSessionReport
      .run({
        sessionId: session.sessionId,
        activityDate: activityDate,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to omit session report',
            variant: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Omit session report success',
            variant: 'success',
          });
          onEdit();
        }
      });
  };

  return (
    <>
      <CampfireAccordion
        title={sessionDisplayInfo.displayName}
        subTitle={publishedRoster ? (session.autoReport ? 'Report Generating...' : 'No Report Required') : 'Incomplete'}
        icon={publishedRoster ? session.autoReport ? <WatchLaterSharp /> : <RemoveCircle /> : <Cancel />}
        onClick={() => (!publishedRoster ? setDialogOpen(true) : null)}
        color={publishedRoster ? 'cancelled' : 'incomplete'}
        disabled={publishedRoster}
      />
      {dialogOpen && (
        <ActivityReportDetailDialog
          session={session}
          activityName={activityName}
          activityDate={activityDate}
          publishedRoster={publishedRoster}
          isEditting
          setIsEditting={() => {}}
          onEdit={onEdit}
          dialogOpen={dialogOpen}
          onDialogClose={onDialogClose}
          dialogHeaderOption={<SessionReportOption onOmit={onOmit} />}
          isAdHoc={isAdHoc}
          orgAllowCICO={orgAllowCICO}
        />
      )}
    </>
  );
}

export function NulledCICOSessionReport({
  activityName,
  sessionReport,
  session,
  activityDate,
  publishedRoster,
  onEdit,
  isAdHoc,
  orgAllowCICO,
}: CompletedSessionReportProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const onDialogClose = () => setDialogOpen(false);
  const [isEditting, setIsEditting] = React.useState(false);

  // Get flexible session display info
  const sessionDisplayInfo = getFlexibleSessionDisplayName(session, activityDate);

  return (
    <>
      <CampfireAccordion
        title={sessionDisplayInfo.displayName}
        subTitle={publishedRoster ? 'Check-Ins Completed' : 'Check-Outs Missing'}
        icon={<CheckCircle />}
        onClick={() => setDialogOpen(true)}
        color='completed'
      />
      {dialogOpen && (
        <ActivityReportDetailDialog
          session={session}
          activityName={activityName}
          activityDate={activityDate}
          publishedRoster={publishedRoster}
          sessionReport={sessionReport}
          isEditting={isEditting}
          setIsEditting={setIsEditting}
          onEdit={onEdit}
          dialogOpen={dialogOpen}
          onDialogClose={onDialogClose}
          isAdHoc={isAdHoc}
          orgAllowCICO={orgAllowCICO}
        />
      )}
    </>
  );
}

function IndividualReportsSessionReport({
  session,
  sessionReport,
  onEdit,
  activityDate,
  activityName,
  publishedRoster,
  orgAllowCICO,
}: CompletedSessionReportProps) {
  const [expanded, setExpanded] = React.useState(false);
  const [mainDialogOpen, setMainDialogOpen] = React.useState(false);
  const [isEditting, setIsEditting] = React.useState(false);
  const [selectedIndividualReport, setSelectedIndividualReport] = React.useState<IndividualSessionReportType | null>(
    null
  );
  const [isEditingIndividual, setIsEditingIndividual] = React.useState(false);
  const [submitForVolunteer, setSubmitForVolunteer] = React.useState<{
    volunteerId: string;
    profile: { preferredName: string; lastName: string; avatarUrl: string | null };
  } | null>(null);

  const sessionDisplayInfo = getFlexibleSessionDisplayName(session, activityDate);
  const individualReports = sessionReport?.individualSessionReports || [];

  const rosteredVolunteers =
    publishedRoster?.rosterings
      ?.filter((r) => r.sessionRosterings.find((sr) => sr.session.sessionId === session.sessionId))
      .map((r) => r.volunteer) || [];

  const submittedVolunteerIds = new Set(individualReports.map((r) => r.volunteer.volunteerId));
  const knownVolunteerIds = new Set(rosteredVolunteers.map((v) => v.volunteerId));

  const cicoVolunteers = (sessionReport?.CICOs || [])
    .filter(
      (c) =>
        c.volunteer &&
        !knownVolunteerIds.has(c.volunteer.volunteerId) &&
        !submittedVolunteerIds.has(c.volunteer.volunteerId)
    )
    .map((c) => c.volunteer);
  const attendanceVolunteers = (sessionReport?.attendances || [])
    .filter(
      (a): a is Extract<AttendanceType, { __typename: 'VOLUNTEER_VolunteerAttendanceType' }> =>
        a.__typename === 'VOLUNTEER_VolunteerAttendanceType' &&
        !!a.volunteer &&
        !knownVolunteerIds.has(a.volunteer.volunteerId) &&
        !submittedVolunteerIds.has(a.volunteer.volunteerId)
    )
    .map((a) => a.volunteer);

  const allVolunteers = [...rosteredVolunteers, ...cicoVolunteers, ...attendanceVolunteers];
  const missingVolunteers = allVolunteers.filter((v) => !submittedVolunteerIds.has(v.volunteerId));

  const classes = useStyles(individualReports.length > 0 ? 'completed' : 'incomplete');

  return (
    <Box>
      <Box className={classes.container} onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
        {individualReports.length > 0 ? <CheckCircle className={classes.icon} /> : <Cancel className={classes.icon} />}
        <Box className={classes.textContainer}>
          <Typography variant='h5'>{sessionDisplayInfo.displayName}</Typography>
          <Typography variant='body1'>
            {allVolunteers.length > 0
              ? `${individualReports.length}/${allVolunteers.length} submitted`
              : `${individualReports.length} individual report${individualReports.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        <IconButton
          className={classes.button}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box pl={4} pr={1} py={0.5} display='flex' flexDirection='column' style={{ gap: 4 }}>
          {sessionReport && (
            <Box
              display='flex'
              alignItems='center'
              px={1.5}
              py={1}
              style={{ cursor: 'pointer', borderRadius: 4, backgroundColor: '#F0F9FF' }}
              onClick={() => {
                setMainDialogOpen(true);
                setIsEditting(false);
              }}
            >
              <EditOutlined style={{ fontSize: 14, color: '#0EA5E9', marginRight: 8 }} />
              <Typography variant='body2' style={{ fontWeight: 600, fontSize: 13, color: '#0369A1', flex: 1 }}>
                {'Session Report'}
              </Typography>
              <ChevronRight style={{ fontSize: 16, color: '#38BDF8' }} />
            </Box>
          )}
          {individualReports.map((report) => (
            <Box
              key={report.individualSessionReportId}
              display='flex'
              alignItems='center'
              px={1.5}
              py={1}
              style={{ cursor: 'pointer', borderRadius: 4, backgroundColor: '#ECFDF5' }}
              onClick={() => setSelectedIndividualReport(report)}
            >
              <Box style={{ position: 'relative', marginRight: 10 }}>
                <Avatar
                  preferredName={report.volunteer.profile.preferredName}
                  lastName={report.volunteer.profile.lastName}
                  avatarUrl={report.volunteer.profile.avatarUrl}
                  size={22}
                />
                <CheckCircle
                  style={{
                    fontSize: 11,
                    color: '#34d399',
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    background: 'white',
                    borderRadius: '50%',
                  }}
                />
              </Box>
              <Typography variant='body2' style={{ fontWeight: 500, fontSize: 13, color: '#23735C', flex: 1 }}>
                {`${report.volunteer.profile.preferredName} ${report.volunteer.profile.lastName}`}
              </Typography>
              <ChevronRight style={{ fontSize: 16, color: '#76ddac' }} />
            </Box>
          ))}
          {missingVolunteers.map((vol) => (
            <Box
              key={vol.volunteerId}
              display='flex'
              alignItems='center'
              px={1.5}
              py={1}
              style={{ cursor: 'pointer', borderRadius: 4, backgroundColor: '#FEF2F2' }}
              onClick={() => setSubmitForVolunteer(vol)}
            >
              <Box style={{ position: 'relative', marginRight: 10 }}>
                <Avatar
                  preferredName={vol.profile.preferredName}
                  lastName={vol.profile.lastName}
                  avatarUrl={vol.profile.avatarUrl}
                  size={22}
                />
                <Cancel
                  style={{
                    fontSize: 11,
                    color: '#F87171',
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    background: 'white',
                    borderRadius: '50%',
                  }}
                />
              </Box>
              <Typography variant='body2' style={{ fontWeight: 500, fontSize: 13, color: '#991B1B', flex: 1 }}>
                {`${vol.profile.preferredName} ${vol.profile.lastName}`}
              </Typography>
              <ChevronRight style={{ fontSize: 16, color: '#ed9b9b' }} />
            </Box>
          ))}
          {individualReports.length === 0 && missingVolunteers.length === 0 && !sessionReport && (
            <Box
              display='flex'
              alignItems='center'
              px={1.5}
              py={1}
              style={{ cursor: 'pointer', borderRadius: 4, backgroundColor: '#FEF2F2' }}
              onClick={() => {
                setMainDialogOpen(true);
                setIsEditting(true);
              }}
            >
              <Cancel style={{ fontSize: 14, color: '#F87171', marginRight: 8 }} />
              <Typography variant='body2' style={{ fontWeight: 500, fontSize: 13, color: '#991B1B', flex: 1 }}>
                {'No reports yet — click to start'}
              </Typography>
              <ChevronRight style={{ fontSize: 16, color: '#ed9b9b' }} />
            </Box>
          )}
        </Box>
      </Collapse>
      {mainDialogOpen && (
        <ActivityReportDetailDialog
          session={session}
          activityName={activityName}
          activityDate={activityDate}
          publishedRoster={publishedRoster}
          sessionReport={sessionReport}
          isEditting={isEditting}
          setIsEditting={setIsEditting}
          onEdit={onEdit}
          dialogOpen={mainDialogOpen}
          onDialogClose={() => setMainDialogOpen(false)}
          forceMainReport
          orgAllowCICO={orgAllowCICO}
        />
      )}
      {selectedIndividualReport &&
        (() => {
          const volId = selectedIndividualReport.volunteer.volunteerId;
          const hasCICOs = (sessionReport?.CICOs?.length ?? 0) > 0;
          const effectiveAllowCICO =
            hasCICOs || sessionReport?.allowCICO || session.activity?.allowCICO || orgAllowCICO || false;
          const cicoRecord = effectiveAllowCICO
            ? sessionReport?.CICOs?.find((c) => c.volunteer?.volunteerId === volId)
            : undefined;
          const existingCICO = cicoRecord
            ? {
                checkIn: cicoRecord.checkIn ? unpackToTime(cicoRecord.checkIn).toJSDate() : ('' as Date | ''),
                checkOut: cicoRecord.checkOut ? unpackToTime(cicoRecord.checkOut).toJSDate() : ('' as Date | ''),
              }
            : undefined;
          const volAttendance = !effectiveAllowCICO
            ? sessionReport?.attendances?.some(
                (a) => a.__typename === 'VOLUNTEER_VolunteerAttendanceType' && a.volunteer?.volunteerId === volId
              ) ?? false
            : undefined;

          return (
            <Dialog
              open
              onClose={() => {
                setSelectedIndividualReport(null);
                setIsEditingIndividual(false);
              }}
              fullWidth
              maxWidth='sm'
            >
              <DialogTitle>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Typography variant='h6'>
                    {`${selectedIndividualReport.volunteer.profile.preferredName}'s Report`}
                  </Typography>
                  <Box display='flex' alignItems='center'>
                    {!isEditingIndividual && (
                      <TabletButton
                        variant='outlined'
                        color='primary'
                        startIcon={<EditOutlined style={{ fontSize: 14 }} />}
                        style={{ boxSizing: 'border-box', borderRadius: '4px', padding: '2px 8px', fontWeight: 600 }}
                        onClick={() => setIsEditingIndividual(true)}
                      >
                        Edit Report
                      </TabletButton>
                    )}
                    <IconButton
                      onClick={() => {
                        setSelectedIndividualReport(null);
                        setIsEditingIndividual(false);
                      }}
                      size='small'
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent>
                <IndividualSessionReportForm
                  session={session}
                  activityDate={activityDate}
                  existingFieldValues={selectedIndividualReport.fieldValues}
                  readOnly={!isEditingIndividual}
                  allowCICO={effectiveAllowCICO}
                  existingCICO={existingCICO}
                  existingAttendance={volAttendance}
                  volunteerId={volId}
                  volunteerProfile={{
                    preferredName: selectedIndividualReport.volunteer.profile.preferredName,
                    lastName: selectedIndividualReport.volunteer.profile.lastName,
                    avatarUrl: selectedIndividualReport.volunteer.profile.avatarUrl,
                  }}
                  onSubmit={() => {
                    setSelectedIndividualReport(null);
                    setIsEditingIndividual(false);
                    onEdit();
                  }}
                  onCancel={() => setIsEditingIndividual(false)}
                />
              </DialogContent>
            </Dialog>
          );
        })()}
      {submitForVolunteer &&
        (() => {
          const submitVolId = submitForVolunteer.volunteerId;
          const submitHasCICOs = (sessionReport?.CICOs?.length ?? 0) > 0;
          const submitAllowCICO =
            submitHasCICOs || sessionReport?.allowCICO || session.activity?.allowCICO || orgAllowCICO || false;
          const submitCicoRecord = submitAllowCICO
            ? sessionReport?.CICOs?.find((c) => c.volunteer?.volunteerId === submitVolId)
            : undefined;
          const submitExistingCICO = submitCicoRecord
            ? {
                checkIn: submitCicoRecord.checkIn
                  ? unpackToTime(submitCicoRecord.checkIn).toJSDate()
                  : ('' as Date | ''),
                checkOut: submitCicoRecord.checkOut
                  ? unpackToTime(submitCicoRecord.checkOut).toJSDate()
                  : ('' as Date | ''),
              }
            : undefined;
          const submitAttendance = !submitAllowCICO
            ? sessionReport?.attendances?.some(
                (a) => a.__typename === 'VOLUNTEER_VolunteerAttendanceType' && a.volunteer?.volunteerId === submitVolId
              ) ?? false
            : undefined;
          return (
            <Dialog open onClose={() => setSubmitForVolunteer(null)} fullWidth maxWidth='sm'>
              <DialogTitle>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Typography variant='h6'>
                    {`Submit for ${submitForVolunteer.profile.preferredName} ${submitForVolunteer.profile.lastName}`}
                  </Typography>
                  <IconButton onClick={() => setSubmitForVolunteer(null)} size='small'>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                <IndividualSessionReportForm
                  session={session}
                  activityDate={activityDate}
                  volunteerId={submitVolId}
                  allowCICO={submitAllowCICO}
                  existingCICO={submitExistingCICO}
                  existingAttendance={submitAttendance}
                  volunteerProfile={{
                    preferredName: submitForVolunteer.profile.preferredName,
                    lastName: submitForVolunteer.profile.lastName,
                    avatarUrl: submitForVolunteer.profile.avatarUrl,
                  }}
                  onSubmit={() => {
                    setSubmitForVolunteer(null);
                    onEdit();
                  }}
                  onCancel={() => setSubmitForVolunteer(null)}
                />
              </DialogContent>
            </Dialog>
          );
        })()}
    </Box>
  );
}

type SessionReportProps = {
  variant: 'completed' | 'incomplete' | 'cancelled' | 'null' | 'null-cico' | 'ad-hoc-available' | 'individual';
  session: SessionType;
  sessionReport?: SessionReportType;
  activityDate: string;
  activityName?: string;
  publishedRoster: PublishedRosterType | null;
  onEdit?: () => void;
  isAdHoc?: boolean;
  orgAllowCICO?: boolean;
};
export function SessionReport({ variant, ...remain }: SessionReportProps) {
  switch (variant) {
    case 'individual':
      return <IndividualReportsSessionReport {...(remain as CompletedSessionReportProps)} />;
    case 'completed':
      return <CompletedSessionReport {...(remain as CompletedSessionReportProps)} />;
    case 'incomplete':
      return <IncompletedSessionReport {...(remain as IncompleteReportProps)} variant='incomplete' />;
    case 'ad-hoc-available':
      return <IncompletedSessionReport {...(remain as IncompleteReportProps)} variant='ad-hoc-available' />;
    case 'null':
      return <NulledSessionReport {...(remain as IncompleteReportProps)} />;
    case 'null-cico':
      return <NulledCICOSessionReport {...(remain as CompletedSessionReportProps)} />;
    default:
      return <CancelledSessionReport {...(remain as CancelledReportProps)} />;
  }
}

export { SessionReportType };
